// packages/api/src/axios/config.ts

import { addBreadcrumb, captureException } from '@qse/sentry'
import { forceLogout, getKeycloakToken, isKeycloakAuth } from '@qse/utils'
import * as Sentry from '@sentry/react'
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth'
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { toast } from 'sonner'

import { ApiError, UnknownError } from './errors'

let api: AxiosInstance
let currentTenantId: string | null = null
let isLogoutInProgress = false

const getJwtToken = async () => {
  // Local Keycloak (Cognito replacement): use the stored direct-grant token.
  if (isKeycloakAuth()) {
    return (await getKeycloakToken()) ?? null
  }

  try {
    const session = await fetchAuthSession()

    return session?.tokens?.accessToken?.toString()
  } catch (error) {
    console.warn('Failed to fetch auth session:', error)

    return null
  }
}

/**
 * Enhanced token retrieval that ensures session is available
 * This is particularly important after page refresh
 */
const getJwtTokenWithRetry = async (retries = 5, delay = 200): Promise<string | null> => {
  for (let i = 0; i < retries; i++) {
    try {
      const token = await getJwtToken()

      if (token) {
        return token
      }

      // If no token, try to get current user to trigger session restoration
      if (i < retries - 1) {
        try {
          await getCurrentUser()
          // Wait a bit longer for session to be restored
          await new Promise(resolve => setTimeout(resolve, delay * (i + 1)))
        } catch (userError) {
          console.warn(`Failed to get current user during token retry ${i + 1}:`, userError)
          // Wait before next attempt
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      }
    } catch (error) {
      console.warn(`Token retrieval attempt ${i + 1} failed:`, error)
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  console.warn('Failed to retrieve token after all retry attempts')

  return null
}

/**
 * Utility function to get the current JWT token
 * Can be used outside of axios interceptors when needed
 */
export const getCurrentToken = async (): Promise<string | null> => {
  return await getJwtTokenWithRetry()
}

/**
 * Utility function to check if user is authenticated
 * Returns true if a valid token exists
 */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    const token = await getJwtTokenWithRetry()

    return !!token
  } catch (error) {
    console.warn('Failed to check authentication status:', error)

    return false
  }
}

/**
 * Debug utility to check if token is being included in requests
 * This can be used to verify the token integration is working
 */
export const debugTokenInclusion = async (): Promise<{
  hasToken: boolean
  tokenLength: number
  tokenPreview: string
}> => {
  try {
    const token = await getJwtTokenWithRetry()

    if (token) {
      return {
        hasToken: true,
        tokenLength: token.length,
        tokenPreview: `${token.substring(0, 10)}...${token.substring(token.length - 10)}`,
      }
    } else {
      return {
        hasToken: false,
        tokenLength: 0,
        tokenPreview: '',
      }
    }
  } catch (error) {
    console.error('Error debugging token inclusion:', error)

    return {
      hasToken: false,
      tokenLength: 0,
      tokenPreview: '',
    }
  }
}

const createAuthError = (message: string) => {
  const authError = new Error(message)

  authError.name = 'AuthenticationError'

  return authError
}

const triggerForceLogout = (message: string) => {
  if (!isLogoutInProgress) {
    isLogoutInProgress = true
    toast.info(message)
    forceLogout()
  }

  return Promise.reject(createAuthError(message))
}

export const setupAxios = (baseURL: string): AxiosInstance => {
  api = axios.create({
    baseURL,
    withCredentials: true,
    timeout: 120000,
    headers: {
      'Content-Type': 'application/json',
      // Tenant ID is now handled dynamically via request interceptor
    },
  })

  api.interceptors.request.use(
    async config => {
      // Create Sentry span for API request tracking
      const method = config.method?.toUpperCase() || 'GET'
      const url = config.url || ''
      const fullUrl = config.baseURL ? `${config.baseURL}${url}` : url

      // Sanitize URL to remove sensitive query parameters
      let sanitizedUrl = fullUrl

      try {
        const urlObj = new URL(fullUrl)
        const sensitiveParams = ['token', 'auth', 'password', 'secret', 'key', 'api_key']

        sensitiveParams.forEach(param => urlObj.searchParams.delete(param))
        sanitizedUrl = urlObj.pathname + urlObj.search
      } catch {
        // If URL parsing fails, use original URL
        sanitizedUrl = url
      }

      // Store span metadata in config for response interceptor
      ;(config as any).__sentryStartTime = Date.now()
      ;(config as any).__sentrySpanName = `${method} ${sanitizedUrl}`
      ;(config as any).__sentrySpanOp = 'http.client'
      ;(config as any).__sentryMethod = method
      ;(config as any).__sentryUrl = sanitizedUrl

      // Add breadcrumb for API request (without request body)
      addBreadcrumb(`${method} ${sanitizedUrl}`, 'http', 'info', {
        method,
        url: sanitizedUrl,
      })

      try {
        const jwt = await getJwtTokenWithRetry()

        if (jwt) {
          config.headers.Authorization = `${jwt}`
          console.log(
            `✅ Token included in request to: ${config.method?.toUpperCase()} ${config.url}`,
          )
        } else {
          console.warn(
            `⚠️ No token available for request to: ${config.method?.toUpperCase()} ${config.url}`,
          )
        }
      } catch (error) {
        console.warn('No valid session or failed to retrieve token', error)
      }

      // Set tenant ID header dynamically
      if (currentTenantId) {
        config.headers['x-tenant-id'] = currentTenantId
        console.log(`🏢 Tenant ID set in request: ${currentTenantId}`)
      } else {
        // Use default tenant ID when no specific tenant is set
        config.headers['x-tenant-id'] = '06ce79ab-1478-4e17-92fd-bb4c328717c2'
      }

      return config
    },
    error => {
      // Capture request error
      captureException(error, {
        context: {
          type: 'axios_request_error',
        },
      })

      return Promise.reject(error)
    },
  )

  api.interceptors.response.use(
    (response: AxiosResponse) => {
      const config = response.config as AxiosRequestConfig & {
        __sentryStartTime?: number
        __sentrySpanName?: string
        __sentrySpanOp?: string
        __sentryMethod?: string
        __sentryUrl?: string
      }

      // Create span for successful API response
      if (config.__sentryStartTime && config.__sentrySpanName) {
        const duration = Date.now() - config.__sentryStartTime

        Sentry.startSpan(
          {
            op: config.__sentrySpanOp || 'http.client',
            name: config.__sentrySpanName,
          },
          span => {
            span.setAttribute('http.status_code', response.status)
            span.setAttribute('http.method', config.__sentryMethod || 'GET')
            span.setAttribute('http.url', config.__sentryUrl || '')
            span.setAttribute('http.duration', duration)
            span.setStatus({ code: 1 }) // 1 = OK
          },
        )
      }

      // Add breadcrumb for successful response
      addBreadcrumb(
        `${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
        'http',
        'info',
        {
          method: response.config.method?.toUpperCase(),
          url: response.config.url,
          status_code: response.status,
        },
      )

      return response
    },
    async error => {
      const config = error.config as AxiosRequestConfig & {
        __sentrySpan?: any
        __sentryStartTime?: number
        __sentrySpanName?: string
        __sentrySpanOp?: string
        __sentryMethod?: string
        __sentryUrl?: string
      }
      const status = error.response?.status
      const method = config?.method?.toUpperCase() || 'GET'
      const url = config?.url || ''

      // Create span for failed API response
      if (config?.__sentryStartTime && config?.__sentrySpanName) {
        const duration = Date.now() - config.__sentryStartTime

        Sentry.startSpan(
          {
            op: config.__sentrySpanOp || 'http.client',
            name: config.__sentrySpanName,
          },
          span => {
            if (status) {
              span.setAttribute('http.status_code', status)
            }
            span.setAttribute('http.method', config.__sentryMethod || method)
            span.setAttribute('http.url', config.__sentryUrl || url)
            span.setAttribute('http.duration', duration)
            span.setStatus({ code: 2 }) // 2 = ERROR
          },
        )
      }

      // Add breadcrumb for failed response
      addBreadcrumb(
        `${method} ${url} - ${status || 'Network Error'}`,
        'http',
        status && status >= 500 ? 'error' : 'warning',
        {
          method,
          url,
          status_code: status,
        },
      )

      // Capture network failures as exceptions (4xx/5xx errors)
      if (!error.response || (status && status >= 400)) {
        const networkError = new Error(
          `API request failed: ${method} ${url}${status ? ` - ${status}` : ''}`,
        )

        networkError.name = status && status >= 500 ? 'ServerError' : 'ClientError'

        captureException(networkError, {
          context: {
            type: 'axios_response_error',
            method,
            url,
            status_code: status,
          },
        })
      }

      const originalRequest = error.config
      const responseData = error.response?.data ?? {}
      const rawMessage =
        responseData?.message ??
        responseData?.error_description ??
        responseData?.error ??
        error.message ??
        ''
      const responseCode = (responseData?.code ?? responseData?.errorCode ?? '').toString()
      const normalizedMessage = rawMessage?.toString().toLowerCase()
      const normalizedCode = responseCode.toLowerCase()

      const isSignupConflictError =
        normalizedCode === 'signup_failed' &&
        normalizedMessage?.includes('user account already exists')

      const isSessionExpiredResponse =
        normalizedMessage.includes('session expired') ||
        normalizedMessage.includes('token expired') ||
        normalizedCode === 'session_expired' ||
        normalizedCode === 'token_expired' ||
        status === 419

      if (isSessionExpiredResponse) {
        console.log('🔒 Session expired response detected - redirecting to login')

        return triggerForceLogout('Your session has expired. Please log in again.')
      }

      if (status === 401 && !originalRequest._retry) {
        originalRequest._retry = true
        try {
          // Try to get a fresh token with retry logic
          const newToken = await getJwtTokenWithRetry()

          if (newToken) {
            // Update the authorization header with the new token
            originalRequest.headers.Authorization = `${newToken}`

            // Retry the original request
            return api(originalRequest)
          } else {
            // No valid token available, force logout
            console.log('🔒 Session expired - redirecting to login')

            // Force logout (will redirect to login with user-friendly message)
            return triggerForceLogout('Your session has expired. Please log in again.')
          }
        } catch (_refreshError) {
          console.log('🔒 Authentication failed - redirecting to login')

          return triggerForceLogout('Please log in to continue.')
        }
      }

      // Handle other error statuses
      if (status === 403) {
        // Auto logout on 403 (Forbidden - user doesn't have permission)
        console.log('🔒 Access denied - redirecting to login')

        return triggerForceLogout('Your session has expired. Please log in again.')
      } else if (status === 404) {
        // Check if 404 is an authentication issue (user not found)
        const errorMessage = error.response?.data?.message?.toLowerCase() || ''
        const errorCode = error.response?.data?.code || ''
        const requestUrl = originalRequest?.url || ''

        if (
          errorMessage.includes('user not found') ||
          errorMessage.includes('user does not exist') ||
          errorCode === 'USER_NOT_FOUND'
        ) {
          console.log('🔒 User session invalid - redirecting to login')
          toast.info('Please log in to continue.')
          forceLogout()

          const authError = new Error('User not found')

          authError.name = 'AuthenticationError'

          return Promise.reject(authError)
        } else if (
          !requestUrl.includes('/files/file') &&
          !requestUrl.includes('/files/folder') &&
          !requestUrl.includes('/files/search') &&
          !requestUrl.includes('/public/invitations/')
        ) {
          // Only show generic "Resource not found" toast for non-file/folder/invitation endpoints
          // File/folder 404s are handled by their respective components with better context
          // Invitation 404s are handled by the check-invitation component with specific error messages
          toast.error('Resource not found.')
        }
      } else if (status >= 500) {
        if (!isSignupConflictError) {
          toast.error('Server error occurred. Please try again later.')
        }
      } else if (status === 422) {
        // Validation errors - let the calling code handle these
        console.warn('Validation error:', error.response?.data)
      }

      if (error.response) {
        throw new ApiError(error)
      } else {
        throw new UnknownError(error)
      }
    },
  )

  return api
}

/**
 * Update the x-tenant-id header for super admin requests
 * @param tenantId - The tenant ID to set in the header
 */
export const updateTenantIdHeader = (tenantId: string) => {
  currentTenantId = tenantId
  console.log(`🏢 Tenant ID updated to: ${tenantId}`)
}

/**
 * Reset the x-tenant-id header to default value
 */
export const resetTenantIdHeader = () => {
  currentTenantId = null
  console.log(`🏢 Tenant ID reset to default`)
}

export { api }
